import Scrollspy from 'react-scrollspy-ez';
import styled from 'styled-components';

import BadgeStyled from '../../../components/Badge';
import ContentHeaderStyled from '../../../components/ContentHeader';
import ExternalLinkGroupStyled from '../../../components/ExternalLinkGroup';
import PaginationStyled from '../../../components/Pagination';
import { REFERENCE_LINKS } from '../../../constants';
import { IconDanger, IconSuccess } from '../../../core';
import commonPageContentStyles from '../../../styles/mixins/_commonPageContentStyles';
import navigationStyles from '../../../styles/mixins/navigationMenuStyles';

interface Props {
  className?: string;
}

const dataExternalLinkGroups = [
  {
    id: 1,
    title: 'Government accessibility resources',
    cards: [
      {
        title: 'Learn about writing in plain language ',
        subtitle: '[digital.gov]',
        linkUrl: REFERENCE_LINKS.digitalGovPlainLanguageGuide,
      },
      {
        title: '18F’s accessibility guide ',
        subtitle: '[guides.18f.org]',
        linkUrl: REFERENCE_LINKS.eighteenFAccessibilityGuide,
      },
      {
        title: 'Accessibility Requirements Tool (ART) for contracting ',
        subtitle: '[section508.gov]',
        linkUrl: REFERENCE_LINKS.section508AccessibilityRequirementsTool,
      },
      {
        title: 'Understanding universal design',
        subtitle: '[section508.gov]',
        linkUrl: REFERENCE_LINKS.section508UniversalDesign,
      },
    ],
  },
];

const NavigationStructure = ({ className = '' }: Props) => (
  <div className={className}>
    <div className="content">
      <ContentHeaderStyled
        title="Navigation"
        breadcrumbs={[
          { label: 'General Components', url: '/general-components' },
          { label: 'Navigation' },
        ]}
      />
      <div className="page-container">
        <div className="page-content">
          <div className="page-content-with-navigation-wrapper">
            <div className="page-content-with-navigation">
              <div className="page-content-item">
                <h2 className="h1" id="overview">
                  Overview
                </h2>
                <p className="mb-40">
                  Navigation elements are critical for guiding users through an interface, helping
                  them find and access different sections or features. Well-designed navigation
                  ensures a smooth, intuitive experience, making it easy for users to understand
                  where they are and how to reach their destination. Effective navigation should be
                  consistent, clear, and organized, with a hierarchy that supports the user’s
                  journey across the platform.
                </p>
                <h3 className="h3" id="tabs">
                  Tabs
                </h3>
                <p className="mb-40">
                  Tabs are a navigation component used to organize content into multiple sections
                  within the same area, allowing users to easily switch between different views or
                  categories. Each tab should clearly label the content it represents, ensuring
                  users understand what information is contained within each section. Tabs should be
                  visually distinct and accessible, with clear states for active, inactive, and
                  hover interactions. When using tabs, make sure the content is logically grouped,
                  and avoid overloading the user with too many options at once.
                </p>
              </div>
              <img
                className="img-responsive mb-40"
                src="/images/general-components-navigation/screen-tabs@2x.webp"
                width={1000}
                height={356}
                alt=""
              />
              <div className="page-content-item">
                <h3 className="h3" id="segmented-buttons">
                  Segmented buttons
                </h3>
                <p className="mb-40">
                  Segmented buttons group multiple related options, allowing users to select one.
                  They are ideal for toggling between views or filtering content. Clearly label each
                  button with text or icons, and ensure the selected option stands out. Use
                  segmented buttons for a small number of mutually exclusive choices, keeping the
                  interface clear and easy to navigate.
                </p>
              </div>
              <img
                className="img-responsive mb-40"
                src="/images/general-components-navigation/screen-segmented-buttons@2x.webp"
                width={1000}
                height={530}
                alt=""
              />
              <div className="page-content-item">
                <h3 className="h3" id="toggle">
                  Toggle
                </h3>
                <p className="mb-40">
                  A toggle allows users to switch between two states, such as &quot;On&quot; or
                  &quot;Off.&quot; It is best used for settings or options that have a clear binary
                  choice. Ensure the toggle’s position and label clearly indicate its current state,
                  providing users with an intuitive and accessible way to make changes. Keep toggles
                  minimal and only use them when the options are distinct and easily understood.
                </p>
              </div>
              <img
                className="img-responsive mb-40"
                src="/images/general-components-navigation/screen-toggle@2x.webp"
                width={1000}
                height={168}
                alt=""
              />
              <div className="page-content-item">
                <h4 className="h4 text-h4-sb" id="toggle-typography">
                  Toggle Typography
                </h4>
                <p className="mb-40">
                  Toggle typography should be clear and legible, using the default body text style
                  to ensure readability. The label should succinctly describe the action or state,
                  with &quot;On&quot; and &quot;Off&quot; states being simple and direct. Use
                  sentence case for labels to maintain consistency with other UI elements, and
                  ensure there is enough contrast between the text and the background to meet
                  accessibility standards.
                </p>
              </div>
              <table className="table table-variants text-h5 mb-40">
                <thead>
                  <tr>
                    <th style={{ width: '15.625rem' }}>Element</th>
                    <th>Font-size (px/rem)</th>
                    <th>Font-weight</th>
                    <th>Type token</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td valign="top">Label text</td>
                    <td>12 / 0.75</td>
                    <td>Regular / 400</td>
                    <td>
                      <div className="role-color">$body/body 1</div>
                    </td>
                  </tr>
                  <tr>
                    <td valign="top">Action text</td>
                    <td>14 / 0.875</td>
                    <td>Regular / 400</td>
                    <td>
                      <div className="role-color">$body/body 1</div>
                    </td>
                  </tr>
                </tbody>
              </table>
              <h2 className="h1 divider" id="specifications">
                Specifications
              </h2>
              <div className="page-content-item">
                <div className="visually-hidden" aria-hidden="true" id="anatomy">
                  Anatomy
                </div>
                <h3 className="h3 mb-40">Anatomy of line tabs</h3>
              </div>
              <div className="number-cards mb-16">
                <div className="number-card">
                  <div className="number-card-img">
                    <span className="number-circle">1</span>
                    <img
                      src="/images/general-components-navigation/screen-anatomy-of-line-tabs-01@2x.webp"
                      width={488}
                      height={340}
                      alt=""
                    />
                  </div>
                </div>
                <div className="number-card">
                  <div className="number-card-img">
                    <span className="number-circle">2</span>
                    <img
                      src="/images/general-components-navigation/screen-anatomy-of-line-tabs-02@2x.webp"
                      width={488}
                      height={340}
                      alt=""
                    />
                  </div>
                </div>
              </div>
              <p className="mb-40 text-body14 anatomy-legend">
                1. Line tabs - A. Label, B. Indicator
                <br />
                2. Icon-only line tabs - B. Indicator, C. Icon
              </p>
              <div className="page-content-item">
                <h3 className="h3" id="anatomy-size">
                  Anatomy &amp; Size of Segmented buttons
                </h3>
                <p className="mb-40">
                  Segmented buttons consist of multiple toggle buttons for users to select from a
                  set of options. Each button has equal height (around 32px) and adjusts in width
                  based on its label. Visual indicators, like color changes or borders, show active
                  selections. Consistent spacing between buttons ensures clarity and ease of use.
                </p>
              </div>
              <img
                className="img-responsive mb-40"
                src="/images/general-components-navigation/screen-anatomy-size@2x.webp"
                width={1000}
                height={387}
                alt=""
              />
              <div className="page-content-item">
                <h4 className="h4 text-h4-sb">Tabs Formatting</h4>
                <p className="mb-40">
                  Tabs are horizontal navigation elements that organize content into separate
                  sections. They should have clear, distinct labels and maintain consistent spacing
                  between each tab. The active tab is visually distinguished, typically by a
                  different color or underlining, to indicate which content section is currently
                  being viewed.
                </p>
              </div>
              <h2 className="h1 divider" id="guidelines">
                Guidelines
              </h2>
              <div className="page-content-item">
                <div className="visually-hidden" aria-hidden="true" id="alignment">
                  Alignment
                </div>
                <h3 className="h3">Alignment of Tabs</h3>
                <p className="mb-40">
                  Much like buttons, alignment of tabs depends on where they appear and whether or
                  not they&apos;re contained within another component. As a general rule, the first
                  label for both line tabs and contained tabs align with the grid and the text
                  below. If tabs are within another component, such as a card, follow the grid that
                  you are using inside the component and align the label with text in the component.
                </p>
                <ol className="numbered-list text-paragraph-large mb-40">
                  <li>
                    <strong>Active Button:</strong> Only one segment can be active at a time. These
                    mutually exclusive Buttons can appear as icon only, text left, or icon left with
                    text.
                  </li>
                  <li>
                    <strong>Default Buttons:</strong> Default control options that can be
                    customized.
                  </li>
                </ol>
              </div>
              <div className="ex-cards">
                <div className="ex-card true">
                  <img
                    className="ex-card-img"
                    src="/images/general-components-navigation/screen-alignment-of-tabs-01@2x.webp"
                    width={488}
                    height={437}
                    alt=""
                  />
                  <div className="ex-card-desc">
                    <BadgeStyled className="success" iconLeft={<IconSuccess />}>
                      Do
                    </BadgeStyled>
                    <p>Do align tab labels with the grid</p>
                  </div>
                </div>
                <div className="ex-card false">
                  <img
                    className="ex-card-img"
                    src="/images/general-components-navigation/screen-alignment-of-tabs-02@2x.webp"
                    width={488}
                    height={437}
                    alt=""
                  />
                  <div className="ex-card-desc">
                    <BadgeStyled className="danger" iconLeft={<IconDanger />}>
                      Don&apos;t
                    </BadgeStyled>
                    <p>Do not align tab container with the grid</p>
                  </div>
                </div>
              </div>
              <div className="page-content-item">
                <div className="visually-hidden" aria-hidden="true" id="usage">
                  Usage
                </div>
                <h3 className="h3">Usage of Segmented buttons</h3>
                <p className="mb-40 text-paragraph-medium">
                  Segmented buttons are used to group related actions or options within a compact
                  interface, allowing users to quickly toggle between choices. They should be used
                  when there are two to four related options that require selection. Ensure the
                  selected state is visually distinct, providing clear feedback to users. Avoid
                  using segmented buttons for more than a few options to maintain clarity and
                  prevent overcrowding.
                </p>
              </div>
              <div className="ex-cards">
                <div className="ex-card true">
                  <img
                    className="ex-card-img"
                    src="/images/general-components-navigation/screen-usage-of-segmented-buttons-01@2x.webp"
                    width={488}
                    height={282}
                    alt=""
                  />
                  <div className="ex-card-desc">
                    <BadgeStyled className="success" iconLeft={<IconSuccess />}>
                      Do
                    </BadgeStyled>
                    <p>
                      Labels should be short and succinct. If a label is too long to fit within its
                      segment, consider using an icon- only variant.
                    </p>
                  </div>
                </div>
                <div className="ex-card false">
                  <img
                    className="ex-card-img"
                    src="/images/general-components-navigation/screen-usage-of-segmented-buttons-02@2x.webp"
                    width={488}
                    height={282}
                    alt=""
                  />
                  <div className="ex-card-desc">
                    <BadgeStyled className="danger" iconLeft={<IconDanger />}>
                      Don&apos;t
                    </BadgeStyled>
                    <p>Wrap or truncate text.</p>
                  </div>
                </div>
              </div>
              <div className="page-content-item">
                <div className="visually-hidden" aria-hidden="true" id="placement">
                  Placement
                </div>
                <h3 className="h3">Segmented buttons Placement</h3>
                <p className="mb-40 text-paragraph-medium">
                  Segmented buttons should be placed in areas where quick selection of related
                  options is needed, such as toolbars or navigation menus. Ensure they are aligned
                  with other UI elements, maintaining consistent spacing. Keep them within reach of
                  the user&apos;s primary actions, avoiding cluttered sections to ensure the
                  interface remains clean and intuitive.
                </p>
              </div>
              <div className="ex-cards">
                <div className="ex-card true">
                  <img
                    className="ex-card-img"
                    src="/images/general-components-navigation/screen-segmented-buttons-placement-01@2x.webp"
                    width={488}
                    height={483}
                    alt=""
                  />
                  <div className="ex-card-desc">
                    <BadgeStyled className="success" iconLeft={<IconSuccess />}>
                      Do
                    </BadgeStyled>
                    <p>Segmented buttons are best used for selecting between 2 and 5 choices.</p>
                  </div>
                </div>
                <div className="ex-card false">
                  <img
                    className="ex-card-img"
                    src="/images/general-components-navigation/screen-segmented-buttons-placement-02@2x.webp"
                    width={488}
                    height={483}
                    alt=""
                  />
                  <div className="ex-card-desc">
                    <BadgeStyled className="danger" iconLeft={<IconDanger />}>
                      Don&apos;t
                    </BadgeStyled>
                    <p>
                      Don&apos;t use more than five segments in a single segmented button. Choices
                      should be scoped. If you have more than five choices, consider using another
                      component, such as chips.
                    </p>
                  </div>
                </div>
              </div>
              <div className="page-content-item">
                <h3 className="h3" id="states">
                  States
                </h3>
                <p className="text-paragraph-medium">
                  Segmented buttons have different states to indicate interaction. These include:
                </p>
                <ol className="numbered-list font-sm mb-40">
                  <li>
                    Default State: The button appears in its normal form, with no selection or
                    hover.
                  </li>
                  <li>
                    Hover State: When the user hovers over the button, the background or border may
                    change to indicate interactivity.
                  </li>
                  <li>
                    Active State: This state is shown when the button is selected, indicating the
                    current active choice.
                  </li>
                  <li>
                    Disabled State: When the button is inactive or unavailable, it appears in a
                    muted color or with reduced opacity to communicate its disabled status.
                  </li>
                </ol>
              </div>
              <div className="number-cards row-gap-24">
                <div className="number-card">
                  <div className="number-card-img">
                    <span className="number-circle">1</span>
                    <img
                      src="/images/general-components-navigation/screen-states-01@2x.webp"
                      width={488}
                      height={288}
                      alt=""
                    />
                  </div>
                </div>
                <div className="number-card">
                  <div className="number-card-img">
                    <span className="number-circle">2</span>
                    <img
                      src="/images/general-components-navigation/screen-states-02@2x.webp"
                      width={488}
                      height={288}
                      alt=""
                    />
                  </div>
                </div>
                <div className="number-card">
                  <div className="number-card-img">
                    <span className="number-circle">3</span>
                    <img
                      src="/images/general-components-navigation/screen-states-03@2x.webp"
                      width={488}
                      height={288}
                      alt=""
                    />
                  </div>
                </div>
                <div className="number-card">
                  <div className="number-card-img">
                    <span className="number-circle">4</span>
                    <img
                      src="/images/general-components-navigation/screen-states-04@2x.webp"
                      width={488}
                      height={288}
                      alt=""
                    />
                  </div>
                </div>
                <div className="number-card">
                  <div className="number-card-img">
                    <span className="number-circle">5</span>
                    <img
                      src="/images/general-components-navigation/screen-states-05@2x.webp"
                      width={488}
                      height={247}
                      alt=""
                    />
                  </div>
                </div>
              </div>
              <h2 className="h1 divider" id="references">
                References
              </h2>
              {dataExternalLinkGroups.map((externalLinkGroup) => (
                <ExternalLinkGroupStyled
                  key={externalLinkGroup.id}
                  title={externalLinkGroup.title}
                  cards={externalLinkGroup.cards}
                />
              ))}
            </div>
            <div>
              <div className="page-navigation">
                <h2>Contents</h2>
                <Scrollspy
                  ids={[
                    'overview',
                    'tabs',
                    'segmented-buttons',
                    'toggle',
                    'specifications',
                    'anatomy',
                    'guidelines',
                    'alignment',
                    'usage',
                    'placement',
                    'states',
                    'references',
                  ]}
                  offset={0}
                  activeItemClassName="active"
                  itemContainerClassName="multilevel-link-list"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <PaginationStyled
        previousLink="/general-components/communication"
        previous="Communication"
        nextLink="/general-components/selection"
        next="Selection"
      />
    </div>
  </div>
);

const Navigation = styled(NavigationStructure)`
  ${commonPageContentStyles}
  ${navigationStyles([2, 3, 4, 6, 8, 9, 10, 11])}

    overflow: unset;
  height: 100%;

  > .content {
    width: 100%;
    padding: 0;
  }

  /* The frame draws the anatomy legend (10502:85493) in the grey that Neutral/text-disabled holds. */
  .page-content p.anatomy-legend {
    color: ${({ theme }) => theme.neutralTextDisabled};
  }
`;

export default Navigation;
